import { syncDownloadEnvs } from '../../../../api';
import { messageError, messageSuccess } from '../../../../components/message';
import { getSiteConfigs } from '../../../../lib/app';
import { wrapRequestArea } from '../../../../lib/http/lib';
import {
  getFirstManageSite,
  getNeedManageSites,
  getSiteDomain,
} from '../../../../lib/multi-site';
import getFormatMessage from '../../../../locales/get-format-message';

import {
  DownloadSyncEnvType,
  IDownLoadVersionMenu,
} from '../../../../model/api-modules';
import { ISiteIDownLoadMenu } from '../download-items';
import { ISiteIDownLoadVersionMenu } from '../download-version';

export type IMultiSubmitRes = Array<{
  res: boolean;
  message: string;
}>;

export const multiSiteSubmitter = async <T,>(
  updateFun: (matchRow: T, site: string) => Promise<boolean>,

  otherSiteMatchFun?: (site: string) => T | undefined,
  createFun?: (site: string) => Promise<boolean>,
  onlyCurrentSite?: boolean
) => {
  const submitSites = onlyCurrentSite
    ? [getFirstManageSite()]
    : getNeedManageSites();

  const submitSite = async (site: string) => {
    if (!otherSiteMatchFun) {
      const mockParams = {} as any;
      const res = await wrapRequestArea(
        () => updateFun(mockParams, site),
        site
      );
      return { res, message: '' };
    }
    const matchRow = otherSiteMatchFun(site);
    if (!matchRow) {
      if (createFun) {
        const res = await wrapRequestArea(() => createFun(site), site);
        return { res, message: '' };
      }
      return {
        res: true,
        message: '',
      };
    }
    const res = await wrapRequestArea(() => updateFun(matchRow, site), site);
    return { res, message: '' };
  };

  const allRes = [];
  for (const site of submitSites) {
    const res = await submitSite(site);
    allRes.push(res);
    if (!res.res) {
      return allRes;
    }
  }

  return allRes;
};

export const getMultiSiteSubmitMessage = (res: IMultiSubmitRes) => {
  return res
    .map((item) => item.message)
    .filter(Boolean)
    .join();
};

export const isMultiSiteSubmitSuccess = (res: IMultiSubmitRes) => {
  return res.every((item) => item.res);
};

export const handleMultiSiteSubmitRes = (
  res: IMultiSubmitRes,
  config?: {
    onSuccess: () => void;
    onError?: () => void;
    successMessage?: string;
    errorMessage?: string;
  }
) => {
  if (isMultiSiteSubmitSuccess(res)) {
    messageSuccess(
      config?.successMessage || getFormatMessage('operation_success')
    );
    config?.onSuccess();
    return true;
  }
  config?.onError?.();
  messageError(
    `${
      config?.errorMessage || getFormatMessage('operation_failed')
    } ${getMultiSiteSubmitMessage(res)}`
  );
  return false;
};

export const getSiteMatchedObjectId = (
  objectID: number,
  site: string,
  allSiteDownLoadList: ISiteIDownLoadMenu
) => {
  const currentSiteDownloadList = allSiteDownLoadList[getFirstManageSite()];
  const downloadObjectTag = currentSiteDownloadList?.find(
    (item) => item.id === objectID
  )?.Tag;
  if (!downloadObjectTag) {
    return;
  }

  return allSiteDownLoadList[site]?.find(
    (item) => item.Tag === downloadObjectTag
  )?.id;
};

export const getDownloadVersionMatcher =
  (
    record: IDownLoadVersionMenu,
    allSiteDownLoadList: ISiteIDownLoadMenu,
    allSiteDownLoadVersionList: ISiteIDownLoadVersionMenu
  ) =>
  (site: string) => {
    const matchedObjectId = getSiteMatchedObjectId(
      record.ObjectID,
      site,
      allSiteDownLoadList
    );
    return allSiteDownLoadVersionList[site]?.find(
      (item) =>
        item.Version === record.Version && item.ObjectID === matchedObjectId
    );
  };

export const getSiteMatchedDownloadVersion = (
  record: IDownLoadVersionMenu,
  site: string,
  allSiteDownLoadList: ISiteIDownLoadMenu,
  allSiteDownLoadVersionList: ISiteIDownLoadVersionMenu
) => {
  const matchedObjectId = getSiteMatchedObjectId(
    record.ObjectID,
    site,
    allSiteDownLoadList
  );
  return allSiteDownLoadVersionList[site]?.find(
    (item) =>
      item.Version === record.Version && item.ObjectID === matchedObjectId
  );
};

export const syncVersionToSite = async (ids: number[], site: string) => {
  const siteConfig = await getSiteConfigs(site);
  const deployRegion = siteConfig?.deployArea;
  const siteDomain = getSiteDomain(site);
  if (!siteDomain || !deployRegion) {
    return { res: false, message: '' };
  }
  const res = await syncDownloadEnvs({
    ids,
    syncToEnvs: [
      {
        domainApi: siteDomain,
        deployRegion,
        envType: DownloadSyncEnvType.production,
        needUploadCos: true,
      },
    ],
  });

  if (res?.code === 102726) {
    return { res: false, message: '有其他同步任务正在运行' };
  }

  if (res?.code === 0) {
    const errorResult: any = [];
    if (res?.data?.syncRes) {
      res.data.syncRes.forEach((item: any) => {
        const resArray = Object.values(item.res);
        if (resArray && resArray.length !== 0) {
          const resErrorItem = resArray.find((it: any) => !!it.apiExecRes);
          let cosError = false;
          resArray.forEach((it: any) => {
            let cosArray: any = [];
            if (it.cos) {
              cosArray = Object.values(it.cos);
            }
            const cosErrorItem = cosArray.find(
              (it: any) => it !== 'uploadSuccess'
            );
            if (cosErrorItem) {
              cosError = true;
            }
          });
          if (resErrorItem || cosError) {
            errorResult.push(item);
          }
        }
      });
    }
    if (errorResult.length !== 0) {
      return { res: false, message: '' };
    }

    return { res: true, message: '' };
  }

  return { res: false, message: '' };
};
